import React, { useState } from 'react';
import { useCustomerAuth } from '../context/CustomerAuthContext';
import './CustomerProfile.css';

const CustomerProfile = ({ isOpen, onClose }) => {
  const {
    currentUser,
    customerProfile,
    orders,
    favorites,
    savedAddresses,
    logout,
    removeSavedAddress,
    setDefaultAddress,
    removeFromFavorites
  } = useCustomerAuth();

  const [activeTab, setActiveTab] = useState('orders');
  const [expandedOrder, setExpandedOrder] = useState(null);

  if (!isOpen || !currentUser) return null;

  const handleLogout = async () => {
    await logout();
    onClose();
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '-';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusInfo = (status) => {
    const statuses = {
      in_progress: { label: 'Preparing', color: '#FF9800', icon: '👨‍🍳' },
      prepared: { label: 'Ready', color: '#4CAF50', icon: '✅' },
      out_for_delivery: { label: 'On the way', color: '#2196F3', icon: '🚚' },
      delivered: { label: 'Delivered', color: '#4CAF50', icon: '🎉' },
      cancelled: { label: 'Cancelled', color: '#f44336', icon: '❌' }
    };
    return statuses[status] || { label: status, color: '#999', icon: '❓' };
  };

  const renderOrders = () => (
    <div className="profile-orders">
      {orders.length === 0 ? (
        <div className="empty-state">
          <span className="empty-icon">📦</span>
          <h3>No orders yet</h3>
          <p>Your order history will appear here</p>
        </div>
      ) : (
        <div className="orders-list">
          {orders.map((order) => {
            const statusInfo = getStatusInfo(order.status);
            const isExpanded = expandedOrder === order.id;

            return (
              <div key={order.id} className="order-card">
                <div
                  className="order-header"
                  onClick={() => setExpandedOrder(isExpanded ? null : order.id)}
                >
                  <div className="order-info">
                    <div className="order-number">Order #{order.orderNumber}</div>
                    <div className="order-date">{formatDate(order.timestamp)}</div>
                  </div>
                  <div className="order-right">
                    <span
                      className="order-status"
                      style={{ background: statusInfo.color }}
                    >
                      {statusInfo.icon} {statusInfo.label}
                    </span>
                    <span className="expand-icon">{isExpanded ? '▲' : '▼'}</span>
                  </div>
                </div>

                {isExpanded && (
                  <div className="order-details">
                    <div className="order-items">
                      {order.items.map((item, idx) => (
                        <div key={idx} className="order-item">
                          <span className={`veg-dot ${item.isVeg ? 'veg' : 'non-veg'}`}>
                            {item.isVeg ? '🟢' : '🔴'}
                          </span>
                          <span className="item-name">{item.name}</span>
                          <span className="item-qty">x{item.quantity}</span>
                          <span className="item-price">₹{item.total}</span>
                        </div>
                      ))}
                    </div>

                    <div className="order-summary">
                      <div className="summary-row">
                        <span>Subtotal</span>
                        <span>₹{order.pricing?.subtotal || 0}</span>
                      </div>
                      {order.pricing?.deliveryCharge > 0 && (
                        <div className="summary-row">
                          <span>Delivery</span>
                          <span>₹{order.pricing.deliveryCharge}</span>
                        </div>
                      )}
                      <div className="summary-row total">
                        <span>Total</span>
                        <span>₹{order.pricing?.total || 0}</span>
                      </div>
                    </div>

                    <div className="order-meta">
                      <span className="meta-item">
                        {order.orderType === 'delivery' ? '🚚 Delivery' : '🏃 Takeaway'}
                      </span>
                      <span className="meta-item">
                        {order.paymentMethod === 'online' ? '💳 Paid Online' : '💵 COD'}
                      </span>
                    </div>

                    {order.status !== 'delivered' && order.status !== 'cancelled' && (
                      <a
                        href={`/track-order?order=${order.orderNumber}&phone=${order.customer?.phone || ''}`}
                        className="track-order-link"
                      >
                        Track Order →
                      </a>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  const renderFavorites = () => (
    <div className="profile-favorites">
      {favorites.length === 0 ? (
        <div className="empty-state">
          <span className="empty-icon">❤️</span>
          <h3>No favorites yet</h3>
          <p>Tap the heart icon on items to save them here</p>
        </div>
      ) : (
        <div className="favorites-list">
          {favorites.map((item, idx) => (
            <div key={idx} className="favorite-item">
              <div className="favorite-info">
                <span className={`veg-dot ${item.isVeg ? 'veg' : 'non-veg'}`}>
                  {item.isVeg ? '🟢' : '🔴'}
                </span>
                <div className="favorite-details">
                  <span className="favorite-name">{item.name}</span>
                  <span className="favorite-price">₹{item.price}</span>
                </div>
              </div>
              <button
                className="remove-favorite-btn"
                onClick={() => removeFromFavorites(item.name)}
                title="Remove from favorites"
              >
                ❤️
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderAddresses = () => (
    <div className="profile-addresses">
      {savedAddresses.length === 0 ? (
        <div className="empty-state">
          <span className="empty-icon">📍</span>
          <h3>No saved addresses</h3>
          <p>Your delivery addresses will be saved here</p>
        </div>
      ) : (
        <div className="addresses-list">
          {savedAddresses.map((addr) => (
            <div key={addr.id} className={`address-card ${addr.isDefault ? 'default' : ''}`}>
              <div className="address-header">
                <span className="address-label">
                  {addr.label === 'Home' && '🏠'}
                  {addr.label === 'Work' && '🏢'}
                  {addr.label === 'Other' && '📍'}
                  {' '}{addr.label}
                </span>
                {addr.isDefault && <span className="default-badge">Default</span>}
              </div>
              <div className="address-content">
                {addr.building && <p className="building">{addr.building}</p>}
                <p className="full-address">{addr.address}</p>
                <p className="location">{addr.location}</p>
              </div>
              <div className="address-actions">
                {!addr.isDefault && (
                  <button
                    className="set-default-btn"
                    onClick={() => setDefaultAddress(addr.id)}
                  >
                    Set as Default
                  </button>
                )}
                <button
                  className="delete-address-btn"
                  onClick={() => removeSavedAddress(addr.id)}
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className="profile-modal-overlay" onClick={onClose}>
      <div className="profile-modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="profile-modal-close" onClick={onClose}>
          &times;
        </button>

        <div className="profile-header">
          <div className="profile-avatar">
            {customerProfile?.name?.charAt(0)?.toUpperCase() || 'U'}
          </div>
          <div className="profile-info">
            <h2>{customerProfile?.name || 'Customer'}</h2>
            <p>{customerProfile?.email}</p>
            <p className="profile-phone">{customerProfile?.phone}</p>
          </div>
        </div>

        <div className="profile-stats">
          <div className="stat-item">
            <span className="stat-value">{orders.length}</span>
            <span className="stat-label">Orders</span>
          </div>
          <div className="stat-item">
            <span className="stat-value">{favorites.length}</span>
            <span className="stat-label">Favorites</span>
          </div>
          <div className="stat-item">
            <span className="stat-value">{savedAddresses.length}</span>
            <span className="stat-label">Addresses</span>
          </div>
        </div>

        <div className="profile-tabs">
          <button
            className={`tab-btn ${activeTab === 'orders' ? 'active' : ''}`}
            onClick={() => setActiveTab('orders')}
          >
            📦 Orders
          </button>
          <button
            className={`tab-btn ${activeTab === 'favorites' ? 'active' : ''}`}
            onClick={() => setActiveTab('favorites')}
          >
            ❤️ Favorites
          </button>
          <button
            className={`tab-btn ${activeTab === 'addresses' ? 'active' : ''}`}
            onClick={() => setActiveTab('addresses')}
          >
            📍 Addresses
          </button>
        </div>

        <div className="profile-tab-content">
          {activeTab === 'orders' && renderOrders()}
          {activeTab === 'favorites' && renderFavorites()}
          {activeTab === 'addresses' && renderAddresses()}
        </div>

        <div className="profile-footer">
          <button className="logout-btn" onClick={handleLogout}>
            Sign Out
          </button>
        </div>
      </div>
    </div>
  );
};

export default CustomerProfile;
